import React from 'react';
import {
  Box,
  Container,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload,
  HourglassEmpty,
} from '@mui/icons-material';
import { BulkDropZone } from '../components/BulkDropZone';
import { useBulkUpload, BulkUploadStatus } from '../hooks/useBulkUpload';
import { useActiveCatalog } from '../hooks/useActiveCatalog';

const statusConfig: Record<BulkUploadStatus, { label: string; color: 'default' | 'primary' | 'success' | 'error' | 'warning' }> = {
  pending: { label: 'Pending', color: 'default' },
  uploading: { label: 'Uploading', color: 'primary' },
  submitted: { label: 'Submitted for Cataloging', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};

const statusIcons: Record<BulkUploadStatus, React.ReactNode> = {
  pending: <HourglassEmpty color="disabled" />,
  uploading: <CloudUpload color="primary" />,
  submitted: <CheckCircle color="success" />,
  failed: <ErrorIcon color="error" />,
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const BulkUpload: React.FC = () => {
  const theme = useTheme();
  const { uploadFiles, uploadQueue, clearCompleted, isUploading } = useBulkUpload();
  const { activeCatalogAliId, availableCatalogs, switchToCatalogAliId } = useActiveCatalog();

  const completedCount = uploadQueue.filter((i) => i.status === 'submitted').length;
  const failedCount = uploadQueue.filter((i) => i.status === 'failed').length;

  const singleCatalog = availableCatalogs.length === 1 ? availableCatalogs[0] : null;
  const dropZoneDisabled = !activeCatalogAliId;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        sx={{
          mb: 2,
          color: theme.palette.primary.main,
          fontFamily: 'Libre Baskerville, serif',
          fontWeight: 'bold',
        }}
      >
        Bulk Upload
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Upload multiple artwork images or a ZIP archive. One catalog object will be
        created per image, titled by the filename. You can edit the details after
        uploading.
      </Typography>

      {availableCatalogs.length === 0 ? (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          No catalog is available for upload. Please contact support.
        </Typography>
      ) : singleCatalog ? (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Uploading to:
          </Typography>
          <Chip
            label={`${singleCatalog.label} (${singleCatalog.catalogAliId})`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      ) : (
        <FormControl sx={{ mb: 3, minWidth: 280 }} size="small">
          <InputLabel id="bulk-upload-catalog-label">Upload to catalog</InputLabel>
          <Select
            labelId="bulk-upload-catalog-label"
            label="Upload to catalog"
            value={activeCatalogAliId ?? ''}
            onChange={(e) => switchToCatalogAliId(e.target.value || null)}
          >
            {availableCatalogs.map((c) => (
              <MenuItem key={c.catalogAliId} value={c.catalogAliId}>
                {c.label} ({c.catalogAliId})
                {c.kind === 'delegated' ? ' — delegated' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <BulkDropZone
        onFilesSelected={uploadFiles}
        isUploading={isUploading}
        disabled={dropZoneDisabled}
        disabledMessage="Select a catalog first"
        data-testid="bulk-drop-zone"
      />

      {uploadQueue.length > 0 && (
        <Paper sx={{ mt: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle1">
              Upload Queue ({uploadQueue.length} files)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {completedCount > 0 && (
                <Chip label={`${completedCount} submitted`} color="success" size="small" />
              )}
              {failedCount > 0 && (
                <Chip label={`${failedCount} failed`} color="error" size="small" />
              )}
              {completedCount > 0 && (
                <Button size="small" onClick={clearCompleted}>
                  Clear Completed
                </Button>
              )}
            </Box>
          </Box>

          <List disablePadding>
            {uploadQueue.map((item) => (
              <ListItem
                key={item.id}
                divider
                sx={{ px: 3 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {statusIcons[item.status]}
                </ListItemIcon>
                <ListItemText
                  primary={item.file.name}
                  secondary={
                    <Box component="span">
                      {formatFileSize(item.file.size)}
                      {item.error && (
                        <Typography component="span" color="error" variant="body2" sx={{ ml: 1 }}>
                          {item.error}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {item.status === 'uploading' && (
                    <Box sx={{ flexGrow: 1 }}>
                      <LinearProgress variant="determinate" value={item.progress} />
                    </Box>
                  )}
                  <Chip
                    label={statusConfig[item.status].label}
                    color={statusConfig[item.status].color}
                    size="small"
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Container>
  );
};

export default BulkUpload;
